import axios, { AxiosHeaders, type AxiosError, type AxiosInstance } from 'axios'
import { ApiError, ErrorType } from '@/types/error'
import { isUnknownRecord } from '@/types/json'
import type { ApiResponse } from '@/types/response'
import { HEADER_KEYS, BUSINESS_CODE } from '@/config/api'
import { cancelManager } from '@/utils/request/request-cancel'
import { tokenRefreshManager } from '@/utils/request/token-refresh'
import type { RequestConfig } from './request-config'

const AUTH_REASON_ACCESS_TOKEN_EXPIRED = 'access_token_expired'
const AUTH_REASON_ACCESS_TOKEN_INVALID = 'access_token_invalid'
const AUTH_REASON_SESSION_REPLACED = 'session_replaced'

/** 将业务码规范化为数字 */
const normalizeBusinessCode = (code?: number | string): number | undefined => {
  if (typeof code === 'number') return code
  if (typeof code === 'string') {
    const n = Number(code)
    return Number.isNaN(n) ? undefined : n
  }
  return undefined
}

/** 从后端响应提取机器可读的认证失败原因。 */
const getAuthReason = (responseData?: unknown): string | undefined => {
  if (!isUnknownRecord(responseData)) {
    return undefined
  }

  const authReason = responseData['auth_reason']
  return typeof authReason === 'string' ? authReason : undefined
}

/** 判断响应是否为当前站点的未认证响应。 */
const isUnauthorized = (status?: number, code?: number | string): boolean => {
  const normalizedCode = normalizeBusinessCode(code)

  return status === BUSINESS_CODE.UNAUTHORIZED || normalizedCode === BUSINESS_CODE.UNAUTHORIZED
}

/** 判断是否应尝试刷新 Token */
const shouldRefreshToken = (
  config: RequestConfig | undefined,
  enableTokenRefresh: boolean,
  status?: number,
  code?: number | string,
  responseData?: unknown
): boolean => {
  return !!(
    config &&
    enableTokenRefresh &&
    !config.skipTokenRefresh &&
    !config._retryAfterRefresh &&
    isUnauthorized(status, code) &&
    getAuthReason(responseData) === AUTH_REASON_ACCESS_TOKEN_EXPIRED
  )
}

/** 判断认证失败是否必须清理本地登录态。 */
const shouldClearAuth = (
  status?: number,
  code?: number | string,
  responseData?: unknown
): boolean => {
  if (!isUnauthorized(status, code)) {
    return false
  }

  const authReason = getAuthReason(responseData)
  return (
    authReason === AUTH_REASON_ACCESS_TOKEN_INVALID || authReason === AUTH_REASON_SESSION_REPLACED
  )
}

/** 使用新 Token 重放原始请求 */
const retryRequestWithFreshToken = async (
  config: RequestConfig,
  axiosInstance: AxiosInstance
): Promise<unknown> => {
  const newToken = await tokenRefreshManager.handleRefresh(axiosInstance)
  const headers = new AxiosHeaders(config.headers as Record<string, string>)

  config._retryAfterRefresh = true
  config._skipDedupOnce = true
  headers.set(HEADER_KEYS.AUTHORIZATION, `Bearer ${newToken}`)
  config.headers = headers

  return axiosInstance.request(config)
}

interface HandleResolvedApiErrorParams {
  config?: RequestConfig
  axiosInstance: AxiosInstance
  enableTokenRefresh: boolean
  status?: number
  code?: number | string
  message?: string
  errorType: ErrorType
  originalError?: unknown
  responseData?: unknown
}

/** 统一处理已解析的 API 错误（401 刷新重试、403 跳转等） */
export const handleResolvedApiError = async ({
  config,
  axiosInstance,
  enableTokenRefresh,
  status,
  code,
  message,
  errorType,
  originalError,
  responseData,
}: HandleResolvedApiErrorParams): Promise<unknown> => {
  const normalizedCode = normalizeBusinessCode(code)
  const resolvedMessage = message || '登录会话已失效，请重新登录'

  // session 被替换或 token 无效时不能刷新，直接清理本地登录态。
  if (shouldClearAuth(status, normalizedCode, responseData)) {
    tokenRefreshManager.redirectToLogin(resolvedMessage)
    throw new ApiError(
      ErrorType.HTTP_ERROR,
      resolvedMessage,
      BUSINESS_CODE.UNAUTHORIZED,
      BUSINESS_CODE.UNAUTHORIZED,
      originalError,
      responseData
    )
  }

  // 401: 尝试刷新 Token
  if (
    config &&
    shouldRefreshToken(config, enableTokenRefresh, status, normalizedCode, responseData)
  ) {
    try {
      return await retryRequestWithFreshToken(config, axiosInstance)
    } catch {
      tokenRefreshManager.redirectToLogin()
      throw new ApiError(
        ErrorType.HTTP_ERROR,
        '登录已过期，请重新登录',
        BUSINESS_CODE.UNAUTHORIZED,
        BUSINESS_CODE.UNAUTHORIZED
      )
    }
  }

  // 403: 无权限
  if (status === BUSINESS_CODE.FORBIDDEN || normalizedCode === BUSINESS_CODE.FORBIDDEN) {
    throw new ApiError(
      ErrorType.HTTP_ERROR,
      message || '没有权限访问该资源',
      BUSINESS_CODE.FORBIDDEN,
      BUSINESS_CODE.FORBIDDEN,
      originalError,
      responseData
    )
  }

  throw new ApiError(
    errorType,
    message || (status ? `请求失败 (${status})` : '请求失败'),
    status,
    normalizedCode,
    originalError,
    responseData
  )
}

/** 处理 axios 错误 */
export const handleError = async (
  error: AxiosError<ApiResponse>,
  axiosInstance: AxiosInstance,
  enableTokenRefresh: boolean
): Promise<unknown> => {
  const config = error.config as RequestConfig

  // 清理已完成的请求
  if (config) {
    cancelManager.clearRequest(
      config.url || '',
      config.method || 'get',
      config.params || config.data
    )
  }

  // 请求取消
  if (axios.isCancel(error)) {
    throw new ApiError(ErrorType.CANCEL_ERROR, '请求已取消')
  }

  // 网络错误
  if (!error.response) {
    throw new ApiError(
      ErrorType.NETWORK_ERROR,
      '网络连接失败，请检查网络设置',
      undefined,
      undefined,
      error
    )
  }

  const { response } = error
  const { status, data } = response
  const responseMessage = typeof data?.message === 'string' ? data.message : undefined

  return handleResolvedApiError({
    config,
    axiosInstance,
    enableTokenRefresh,
    status,
    code: data?.code,
    message: responseMessage || `请求失败 (${status})`,
    errorType: ErrorType.HTTP_ERROR,
    originalError: error,
    responseData: data?.data,
  })
}
